import React, {useState, useEffect, useMemo} from 'react';
import { ServerFailed } from '../errors/serverFailed'

import { Typography, Grid } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';

import { PremiumSermon } from '../../entities/PremiumSermon';
import { PremiumSermonService } from '../../services/PremiumSermonService';
import { orderBy } from '../../utilities/orderBy';
import { Search } from '../../utilities/PremiumSermonSearch';
import SearchIcon from '@material-ui/icons/Search';
import { GridGrow } from '../../components/GridGrow';
import { HorizontalCentered } from '../../components/HorizontalCentered';
import { VerticalCenter } from '../../components/VerticalCenter';
import { routes } from '../../routes';
import { SortByDropDown, SortCatagories } from './Views/Components/SortByDropDown';
import { PremiumContentListCard } from './Views/Components/PremiumContentListCard';
import { useIsMobile } from '../../components/Mui3WidthProvider';
import { useLibrarySearch } from './LibraryPage';
import { PageTitle } from '../../components/PageTitle';

interface Props {
}

const sortSermons = (sermonsToSort:PremiumSermon[], sortBy: SortCatagories) => {
    switch(sortBy){
        case "Name":
            return sermonsToSort.sort(orderBy.string(sermon => sermon.name, "Ascending"));
        case "Popularity":
            return sermonsToSort.sort(orderBy.number(sermon => sermon.totalActivations, "Descending"))
        default:
            return sermonsToSort.sort(orderBy.date(sermon => sermon.dateCreated, "Descending"));
    }
}

export function PremiumSermons(props:Props){
    const isMobile = useIsMobile();
    const searchContext = useLibrarySearch();
    const { searchValue } = searchContext
    
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [serverFailed, setServerFailed] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState<SortCatagories>("Newest");
    const [enabledPremiumSermons, setEnabledPremiumSermons ] = useState<PremiumSermon[]>([])
    
    const displayPremiumSermons = useMemo(() => {
        const sortedSermons = sortSermons([...enabledPremiumSermons], sortBy);
        return Search(searchValue, sortedSermons);
    }, [enabledPremiumSermons, searchValue, sortBy])

    useEffect(() => {
        async function loadPremiumSermons() {
            setServerFailed(false)
            setIsLoading(true);
            const response = await PremiumSermonService.getAllEnabled();
            setIsLoading(false);

            if(response.success) return setEnabledPremiumSermons(response.data)
            else return setServerFailed(true)
        }
        loadPremiumSermons();
    }, [])

    return (
        <div style={{height: "100%"}}>
            <PageTitle title="Sermon Maker - Premium Sermons" />
            { !isMobile &&
                <Grid container style={{height: 45, borderBottom: "1px solid rgb(225, 225, 225)"}}>
                    <Grid container item md={8} lg={9} alignItems="center">
                            <Grid item>
                                <Typography style={{ color: "#5f6368", fontSize: "18px", fontWeight: 500, fontFamily: "Roboto, Helvetica"}}>
                                    Premium Sermons
                                </Typography>
                            </Grid>
                        <GridGrow />
                        <Grid item>
                            <SortByDropDown
                                sortBy={sortBy}
                                sortPremiumContent={true}
                                handleSortByChange={(sort) => setSortBy(sort)}
                                />
                        </Grid>
                    </Grid>
                </Grid>
            }
            <Grid container style={{height: isMobile ? "100%" : "calc(100% - 45px)"}}>
                {serverFailed
                ?
                    <Grid container style={{height: "100%"}}>
                        <VerticalCenter>
                            <HorizontalCentered>
                                <ServerFailed errorMessage="We're sorry, but we're having trouble loading Premium Sermons." />
                            </HorizontalCentered>
                        </VerticalCenter>
                    </Grid>
                :
                <Grid item xs={12} style={{height: "100%", overflowY: "auto", overflowX: "hidden"}}>
                    {isLoading
                    ?
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress style={{ marginTop: "50px" }} />
                            <br />
                            <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
                                {searchValue ? "Searching" : "Loading"} Premium Sermons...
                            </Typography>
                        </div>
                    : displayPremiumSermons.length === 0
                    ?
                        <VerticalCenter>
                            <HorizontalCentered>
                                <HorizontalCentered>
                                    <SearchIcon style={{ width: "150px", height: "150px", color: "#CCC"}}/>
                                </HorizontalCentered>
                                <Typography 
                                    style={{ 
                                        fontFamily: "Roboto, Helvetica", fontSize: "12px", marginTop: "12px", color: "#5f6368"
                                }}
                                >No matches found for {searchValue}. Please try again.</Typography>
                            </HorizontalCentered>
                        </VerticalCenter>
                    :
                        <Grid container spacing={3} style={{ padding: 20 }}>
                            {displayPremiumSermons.map((sermon) => {
                                return (
                                    <PremiumContentListCard 
                                        key={sermon.id}
                                        id={sermon.id}
                                        name={sermon.name}
                                        shortDescription={sermon.shortDescription}
                                        imageUrl={sermon.thumbnailUrl}
                                        detailPageUrl={routes.app.resolve.premiumSermonDetailPage(sermon.id.toString())}
                                    />
                                );
                            })}
                        </Grid>
                    }
                </Grid>
                }
            </Grid>
        </div>
    );
}